import { of } from "rxjs";
import { catchError, filter, map, mergeMap, tap } from "rxjs/operators";
import { StateObservable } from "redux-observable";
import { LOCATION_CHANGE } from "connected-react-router";

import { Effect } from "../../../../../redux/types/effect.type";
import { EffectActionsObservable } from "../../../../../redux/types/effect-actions-observable.type";
import { RootState } from "../../../../../redux/store";
import { isActionType } from "../../../../../redux/action.functions";

import * as workOrderStore from "../../../store/work-order.store";

import * as service from "../services/work-order-assets-tab.service";

import * as actions from "./work-order-assets-tab.actions";
import * as store from "./work-order-assets-tab.store";
import { WorkOrderAssetsTabActions } from "./work-order-assets-tab.actions";

const fetchAssets: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(WorkOrderAssetsTabActions.FetchAssets)),
    map((action) => action.payload),
    mergeMap((workOrderId) =>
      service.fetchAssets(workOrderId).pipe(
        map((assets) => actions.fetchAssetsSuccess(assets)),
        catchError((error) => of(actions.fetchAssetsFailure(error)))
      )
    )
  );

const fetchAssetsSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(WorkOrderAssetsTabActions.FetchAssetsSuccess)),
    map((action) => action.payload),
    map(actions.setAssets)
  );

const uploadAssets: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(WorkOrderAssetsTabActions.UploadAssets)),
    map((action) => ({
      assets: action.payload,
      workOrder: workOrderStore.getWorkOrder(state$.value),
    })),
    mergeMap(({ assets, workOrder }) =>
      service.uploadAssets(assets, workOrder!.id).pipe(
        map((assets) => actions.uploadAssetsSuccess(assets)),
        catchError((error) => of(actions.uploadAssetsFailure(error)))
      )
    )
  );

const uploadAssetsSuccess: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(WorkOrderAssetsTabActions.UploadAssetsSuccess)),
    map((action) => ({
      newAssets: action.payload,
      currentAssets: store.getAssets(state$.value),
    })),
    tap(({ newAssets }) => workOrderStore.updateStatus(newAssets[0].workOrderStatus)),
    map(({ newAssets, currentAssets }) =>
      actions.setAssets(service.addAssetsToAssets(newAssets, currentAssets))
    )
  );

const deleteAsset: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(WorkOrderAssetsTabActions.DeleteAsset)),
    map((action) => action.payload),
    mergeMap((assetId) =>
      service.deleteAsset(assetId).pipe(
        map(() => actions.deleteAssetSuccess(assetId)),
        catchError((error) => of(actions.deleteAssetFailure(error)))
      )
    )
  );

const deleteAssetSuccess: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(WorkOrderAssetsTabActions.DeleteAssetSuccess)),
    map((action) => ({
      assetId: action.payload,
      assets: store.getAssets(state$.value),
    })),
    map(({ assetId, assets }) => actions.setAssets(service.removeAssetFromAssets(assetId, assets)))
  );

const reset: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter((action) => action.type === LOCATION_CHANGE),
    map(() => actions.reset())
  );

export const WorkOrderAssetsTabEffects = [
  fetchAssets,
  uploadAssets,
  deleteAsset,
  uploadAssetsSuccess,
  fetchAssetsSuccess,
  deleteAssetSuccess,
  reset,
];
